import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/utils";
import { Search } from "lucide-react";
import * as React from "react";

// Outer container for selector (nav + sidebar + main)
export const Selector = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => {
  let nav = null;
  let sidebar = null;
  let search = null;
  let results = null;
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    const displayName = (child.type as any).displayName;
    if (displayName === "SelectorNav") nav = child;
    else if (displayName === "SelectorSidebar") sidebar = child;
    else if (displayName === "SelectorSearch") search = child;
    else if (displayName === "SelectorResults") results = child;
  });

  return (
    <div
      ref={ref}
      className={cn("flex @container h-full", className)}
      {...props}
    >
      <div className="flex h-full @lg:flex-row flex-col w-full flex-1">
        <div className="flex @lg:flex-row flex-0 flex-col h-full">
          {nav}
          {sidebar}
        </div>
        <div className="flex flex-col flex-1 h-full min-w-0">
          {search}
          {results}
        </div>
      </div>
    </div>
  );
});
Selector.displayName = "Selector";

// Sidebar section (list/tree)
export const SelectorSidebar = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => (
  <div
    data-name="selector-sidebar"
    ref={ref}
    className={cn(
      // default styles
      "w-full bg-muted/30 border-b h-full overflow-y-auto max-h-[300px]",
      // large container styles
      "@lg:w-48 @lg:border-r @lg:border-b-0 @lg:max-h-none",
      className,
    )}
    {...props}
  >
    {children}
  </div>
));
SelectorSidebar.displayName = "SelectorSidebar";

// Search bar
interface SelectorSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  // Allow passing other div props except onChange
  [key: string]: any;
}

export const SelectorSearch = React.forwardRef<
  HTMLDivElement,
  SelectorSearchProps
>(({ value, onChange, placeholder, className, ...props }, ref) => (
  <div ref={ref} className={cn("p-3 border-b", className)} {...props}>
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || "Search..."}
        className="pl-9"
        autoFocus
      />
    </div>
  </div>
));
SelectorSearch.displayName = "SelectorSearch";

// Results list container
export const SelectorResults = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => (
  <div ref={ref} className={cn("overflow-y-auto h-full", className)} {...props}>
    {children}
  </div>
));
SelectorResults.displayName = "SelectorResults";

// Single result item (row/button)
export const SelectorResultItem = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    icon?: React.ReactNode;
    title: string;
    subtitle?: string;
    selected?: boolean;
  }
>(({ icon, title, subtitle, selected, className, ...props }, ref) => (
  <button
    ref={ref}
    className={cn(
      "w-full p-3 flex items-center gap-3 text-left transition-colors overflow-hidden",
      selected
        ? "bg-primary text-primary-foreground"
        : "hover:bg-accent text-foreground",
      className,
    )}
    {...props}
  >
    {icon && (
      <div className="w-10 h-10 shrink-0 rounded-full bg-primary/10 flex items-center justify-center">
        {icon}
      </div>
    )}
    <div className="min-w-0 flex-1">
      <div className="font-medium truncate">{title}</div>
      {subtitle && (
        <div className="text-sm text-muted-foreground truncate">{subtitle}</div>
      )}
    </div>
  </button>
));
SelectorResultItem.displayName = "SelectorResultItem";

// Loading skeleton for results
export const SelectorLoading = () => (
  <div className="p-3 space-y-2">
    {[1, 2, 3].map((i) => (
      <div key={i} className="flex items-center gap-2">
        <Skeleton className="h-10 w-10 rounded-full" />
        <div className="flex-1">
          <Skeleton className="h-4 w-3/4 mb-1" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      </div>
    ))}
  </div>
);

// Empty state for no results
export const SelectorEmpty = ({
  icon,
  message,
}: {
  icon?: React.ReactNode;
  message?: React.ReactNode;
}) => (
  <div className="flex flex-col items-center justify-center h-full text-muted-foreground text-center">
    {icon}
    <p>{message || "No results found"}</p>
  </div>
);

// Nav bar section (high-level navigation)
export const SelectorNav = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      // default styles
      "flex flex-row items-center w-full h-full flex-0",
      // large container styles
      "@lg:flex-col @lg:h-full @lg:flex-0",
      className,
    )}
    {...props}
  >
    <div
      className={cn(
        // default styles
        "w-full border-b bg-muted/40 flex flex-row items-center",
        // large container styles
        "@lg:w-16 @lg:border-b-0 @lg:border-r @lg:flex-col @lg:space-y-2 @lg:h-full",
      )}
    >
      {children}
    </div>
  </div>
));
SelectorNav.displayName = "SelectorNav";

export const SelectorNavIcon = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      // default styles
      "flex items-center justify-center rounded-lg bg-transparent transition-colors",
      // large container styles
      "@lg:w-10 @lg:h-10",
      className,
    )}
    {...props}
  >
    {children}
  </div>
));
SelectorNavIcon.displayName = "SelectorNavIcon";

export const SelectorNavText = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      // default styles
      "text-sm text-center text-muted-foreground pb-0",
      // large container styles
      "@lg:text-xs @lg:pb-1",
      className,
    )}
    {...props}
  >
    {children}
  </div>
));
SelectorNavText.displayName = "SelectorNavText";

export const SelectorNavButton = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    selected?: boolean;
    icon: React.ReactNode;
    children: React.ReactNode;
  }
>(({ selected, icon, children, className, ...props }, ref) => (
  <button
    ref={ref}
    className={cn(
      // default styles
      "group flex flex-row items-center justify-center gap-2 w-full py-2 transition-colors",
      // large container styles
      "@lg:flex-col @lg:py-1",
      selected ? "bg-primary/10" : "hover:bg-accent",
      className,
    )}
    aria-current={selected ? "page" : undefined}
    {...props}
  >
    <SelectorNavIcon>
      {React.isValidElement(icon)
        ? React.cloneElement(icon as React.ReactElement<any, any>, {
            className: cn(
              // default styles
              "w-4 h-4 stroke-2 text-muted-foreground",
              // large container styles
              "@lg:w-6 @lg:h-6",
              (icon as React.ReactElement<any, any>).props?.className,
            ),
          })
        : icon}
    </SelectorNavIcon>
    <SelectorNavText>{children}</SelectorNavText>
  </button>
));
SelectorNavButton.displayName = "SelectorNavButton";
